import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Shield, ShieldOff, UserX, UserCheck, Sparkles, Music, Video, BookOpen, ListMusic, Download, Users as UsersIcon, Trash2 } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { SortableColumn, useTableSort, type SortDirection } from '@/components/ui/SortableColumn';
import { BandMembersModal } from '@/components/BandMembersModal';
import { api } from '@/lib/api';
import { formatNumber } from '@/lib/utils';

// Aba consolidada em 2026-07-08 — antes tinha 2 abas separadas (Lista + Top
// Users). Unificado em uma tabela só com dropdown "Ordenar por". Motivação:
// menos abas, mais alinhado com padrão moderno (Linear/Notion). Backend
// listUsers agora retorna total_plays, então sort client-side cobre ranking.

// ⚠️ Uma conta tem TRÊS situações, não duas.
//
// Até 03/10/2026 este selo lia só `is_suspended`, então uma conta ENCERRADA
// aparecia como "ativo" — visualmente idêntica a uma conta viva. Encerrar
// grava `is_active = false` (e anonimiza username e e-mail), e a API sempre
// mandou esse campo na lista: faltava a tela usá-lo.
//
// Apareceu numa conta de teste encerrada pelo app que seguia marcada "ativo"
// no painel. Num usuário real, a leitura errada seria sobre uma pessoa.
//
// A ordem importa: encerrada vence suspensa, porque suspender quem já saiu
// não muda nada, e o que a tela precisa dizer é que a conta não existe mais.
function situacaoDaConta(u: any): 'encerrada' | 'suspenso' | 'ativo' {
  if (u.is_active === false) return 'encerrada';
  if (u.is_suspended) return 'suspenso';
  return 'ativo';
}

const SELO_DA_SITUACAO: Record<string, string> = {
  encerrada: 'bg-neutral-800 text-neutral-400',
  suspenso:  'bg-red-950 text-red-300',
  ativo:     'bg-emerald-950 text-emerald-300',
};

const ORDER_OPTIONS: { value: string; label: string; direction: SortDirection }[] = [
  { value: 'created_at',      label: 'Mais recentes',     direction: 'desc' },
  { value: 'total_plays',     label: 'Mais ouvidos',      direction: 'desc' },
  { value: 'followers_count', label: 'Mais seguidos',     direction: 'desc' },
  { value: 'publications',    label: 'Mais publicações',  direction: 'desc' },
  { value: 'user',            label: 'Nome A→Z',          direction: 'asc'  },
];

export function Users() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [ageGroup, setAgeGroup] = useState('');
  const [profileType, setProfileType] = useState('');
  const [proStatus, setProStatus] = useState('');
  const [orderBy, setOrderBy] = useState<string>('created_at');
  // Modal de gestão de membros de banda — armazena o user da banda selecionada.
  const [bandModal, setBandModal] = useState<{ id: string; label: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-users', search, ageGroup, profileType, proStatus],
    queryFn: async () => {
      const params: any = { limit: 100 };
      if (search) params.search = search;
      if (ageGroup) params.ageGroup = ageGroup;
      if (profileType) params.profileType = profileType;
      if (proStatus) params.proStatus = proStatus;
      const { data } = await api.get('/admin/users', { params });
      return data?.data;
    },
  });

  const suspend = useMutation({
    mutationFn: (id: string) => api.post(`/admin/users/${id}/suspend`, { reason: 'Suspenso via painel admin' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });
  const unsuspend = useMutation({
    mutationFn: (id: string) => api.post(`/admin/users/${id}/unsuspend`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });
  const toggleMod = useMutation({
    mutationFn: ({ id, isModerator }: { id: string; isModerator: boolean }) =>
      api.patch(`/admin/users/${id}/moderator`, { isModerator }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });
  const deleteUser = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
    onError: (err: any) => {
      alert(err?.response?.data?.error?.message || 'Erro ao excluir usuário.');
    },
  });

  function handleDelete(u: any) {
    const typed = window.prompt(
      `⚠️ EXCLUSÃO PERMANENTE\n\nIsto vai apagar @${u.username} e TODO conteúdo dele (posts, playlists, comentários, etc). Ação irreversível.\n\nDigite o username exato pra confirmar:`,
    );
    if (typed === null) return;
    if (typed.trim() !== u.username) {
      alert('Username não confere — exclusão cancelada.');
      return;
    }
    deleteUser.mutate(u.id);
  }

  const rawUsers: any[] = Array.isArray(data) ? data : [];

  // Determina direção default baseada no orderBy selecionado, mas permite
  // usuário reordenar clicando nas colunas (useTableSort mantém estado).
  const defaultDirection = ORDER_OPTIONS.find((o) => o.value === orderBy)?.direction ?? 'desc';
  const { sorted: users, sort, handleSort } = useTableSort(rawUsers, orderBy, defaultDirection, (row, field) => {
    if (field === 'user') return row.display_name || row.username;
    if (field === 'publications') {
      return (row.tracks_count || 0) + (row.clips_count || 0) + (row.stories_count || 0) + (row.playlists_count || 0);
    }
    if (field === 'status') return situacaoDaConta(row);
    if (field === 'is_pro_subscriber') return row.is_pro_subscriber ? 1 : 0;
    return row[field];
  });

  function handleOrderChange(value: string) {
    setOrderBy(value);
    const target = ORDER_OPTIONS.find((o) => o.value === value);
    if (target && target.value !== sort.field) {
      // Aplica o sort do dropdown chamando o mesmo handler das colunas —
      // toggle é reiniciado; próxima ordenação por essa coluna alterna dir.
      handleSort(target.value);
      if (target.direction === 'asc' && sort.direction === 'desc') handleSort(target.value);
      if (target.direction === 'desc' && sort.direction === 'asc') handleSort(target.value);
    }
  }

  function exportCsv() {
    const headers = ['username', 'display_name', 'email', 'profile_type', 'age_group', 'total_plays', 'followers_count', 'publications', 'is_pro', 'situacao', 'is_suspended', 'created_at'];
    const rows = users.map((u: any) => [
      u.username,
      `"${(u.display_name || '').replace(/"/g, '""')}"`,
      u.email,
      u.profile_type,
      u.age_group,
      String(u.total_plays ?? 0),
      String(u.followers_count ?? 0),
      String((u.tracks_count || 0) + (u.clips_count || 0) + (u.stories_count || 0) + (u.playlists_count || 0)),
      u.is_pro_subscriber ? 'sim' : 'não',
      situacaoDaConta(u),
      u.is_suspended ? 'sim' : 'não',
      u.created_at,
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `usuarios-${orderBy}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <Layout title="Usuários">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <Input
            className="pl-9"
            placeholder="Buscar por username ou e-mail…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={orderBy} onChange={(e) => handleOrderChange(e.target.value)}>
          {ORDER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>Ordenar: {o.label}</option>
          ))}
        </Select>
        <Select value={profileType} onChange={(e) => setProfileType(e.target.value)}>
          <option value="">Tipo: todos</option>
          <option value="solo">Artista solo</option>
          <option value="band">Banda</option>
          <option value="fan">Fã</option>
        </Select>
        <Select value={ageGroup} onChange={(e) => setAgeGroup(e.target.value)}>
          <option value="">Idade: todas</option>
          <option value="adult">Adultos</option>
          <option value="minor">Menores</option>
        </Select>
        <Select value={proStatus} onChange={(e) => setProStatus(e.target.value)}>
          <option value="">Pro: todos</option>
          <option value="pro">Só Pro</option>
          <option value="free">Só free</option>
        </Select>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={users.length === 0}>
          <Download size={14} className="mr-1" />
          CSV
        </Button>
      </div>

      <Card className="p-0 w-full max-w-full overflow-hidden">
        <div className="w-full overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-bg-input text-left text-xs uppercase text-text-secondary">
              <tr>
                <SortableColumn label="User" field="user" sort={sort} onSort={handleSort} />
                <SortableColumn label="E-mail" field="email" sort={sort} onSort={handleSort} />
              <SortableColumn label="Tipo" field="profile_type" sort={sort} onSort={handleSort} />
              <SortableColumn label="Idade" field="age_group" sort={sort} onSort={handleSort} />
              <SortableColumn label="Publicações" field="publications" sort={sort} onSort={handleSort} />
              <SortableColumn label="Plays" field="total_plays" sort={sort} onSort={handleSort} align="right" />
              <SortableColumn label="Fãs" field="followers_count" sort={sort} onSort={handleSort} align="right" />
              <SortableColumn label="Pro" field="is_pro_subscriber" sort={sort} onSort={handleSort} />
              <SortableColumn label="Status" field="status" sort={sort} onSort={handleSort} />
              <th className="px-3 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={10} className="p-8 text-center text-text-muted">Carregando…</td></tr>
            )}
            {users.map((u: any) => (
              <tr key={u.id} className="border-b border-border last:border-0">
                <td className="px-3 py-3">
                  <div className="font-medium text-text-primary">{u.display_name || u.username}</div>
                  <div className="text-xs text-text-muted">@{u.username}</div>
                </td>
                <td className="px-3 py-3 text-text-secondary">
                  <div className="max-w-[220px] truncate" title={u.email}>{u.email}</div>
                </td>
                <td className="px-3 py-3 text-text-secondary">{u.profile_type}</td>
                <td className="px-3 py-3">
                  <span className={`rounded-md px-2 py-0.5 text-xs ${u.age_group === 'minor' ? 'bg-amber-950 text-amber-300' : 'bg-emerald-950 text-emerald-300'}`}>
                    {u.age_group === 'minor' ? 'menor (13-17)' : 'adulto (18+)'}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <div className="font-semibold text-text-primary">
                    {formatNumber((u.tracks_count || 0) + (u.clips_count || 0) + (u.stories_count || 0) + (u.playlists_count || 0))}
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-xs text-text-muted">
                    <span className="inline-flex items-center gap-0.5" title="Músicas">
                      <Music size={11} />{u.tracks_count || 0}
                    </span>
                    <span className="inline-flex items-center gap-0.5" title="Clipes">
                      <Video size={11} />{u.clips_count || 0}
                    </span>
                    <span className="inline-flex items-center gap-0.5" title="Stories">
                      <BookOpen size={11} />{u.stories_count || 0}
                    </span>
                    <span className="inline-flex items-center gap-0.5" title="Playlists">
                      <ListMusic size={11} />{u.playlists_count || 0}
                    </span>
                  </div>
                </td>
                <td className="px-3 py-3 text-right font-semibold text-brand">{formatNumber(u.total_plays || 0)}</td>
                <td className="px-3 py-3 text-right text-text-secondary">{formatNumber(u.followers_count || 0)}</td>
                <td className="px-3 py-3">
                  {u.is_pro_subscriber ? (
                    <span className="inline-flex items-center gap-1 rounded-md bg-brand-bg px-2 py-0.5 text-xs text-brand">
                      <Sparkles size={11} />
                      Pro
                    </span>
                  ) : (
                    <span className="text-xs text-text-muted">—</span>
                  )}
                </td>
                <td className="px-3 py-3">
                  {(() => {
                    const situacao = situacaoDaConta(u);
                    return (
                      <span className={`rounded-md px-2 py-0.5 text-xs ${SELO_DA_SITUACAO[situacao]}`}>
                        {situacao}
                      </span>
                    );
                  })()}
                  {u.is_moderator && (
                    <span className="ml-2 rounded-md bg-brand-bg px-2 py-0.5 text-xs text-brand">admin</span>
                  )}
                </td>
                <td className="px-3 py-3 text-right">
                  <div className="inline-flex gap-1">
                    {u.profile_type === 'band' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        title="Gerenciar membros da banda"
                        onClick={() => setBandModal({ id: u.id, label: u.display_name || u.username })}
                      >
                        <UsersIcon size={14} />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      title={u.is_moderator ? 'Remover admin' : 'Tornar admin'}
                      onClick={() => toggleMod.mutate({ id: u.id, isModerator: !u.is_moderator })}
                    >
                      {u.is_moderator ? <ShieldOff size={14} /> : <Shield size={14} />}
                    </Button>
                    {u.is_suspended ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        title="Reativar usuário"
                        onClick={() => unsuspend.mutate(u.id)}
                      >
                        <UserCheck size={14} />
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        title="Suspender usuário"
                        onClick={() => {
                          if (confirm(`Suspender @${u.username}?`)) suspend.mutate(u.id);
                        }}
                      >
                        <UserX size={14} />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      title="Excluir usuário (permanente)"
                      onClick={() => handleDelete(u)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {!isLoading && users.length === 0 && (
              <tr><td colSpan={10} className="p-8 text-center text-text-muted">Nenhum usuário encontrado.</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </Card>

      {bandModal && (
        <BandMembersModal
          bandUserId={bandModal.id}
          bandLabel={bandModal.label}
          onClose={() => setBandModal(null)}
        />
      )}
    </Layout>
  );
}
